/**
 * Domain hooks for the lockbox read endpoints:
 * - GET `/lockbox/access`        → {@link useLockboxAccessDomain}
 * - GET `/lockbox/by-wallet`     → {@link useLockboxesByWalletDomain}
 * - GET `/lockbox/:address`      → {@link useLockboxDomain}
 * - GET `/lockbox/by-nft-mint`   → {@link useLockboxByNftMintDomain}
 *
 * These return parsed Domain types (amounts as `bigint`) straight from RTK Query with no UI
 * rescaling. Consume the matching `useLockbox*` view hooks in {@link ../view/lockbox} for rendering.
 */

import { skipToken } from "@reduxjs/toolkit/query";

import {
    useLockboxAccessQuery,
    useLockboxByNftMintQuery,
    useLockboxesByWalletQuery,
    useLockboxQuery
} from "../../reducers/lockboxApi";
import { LockboxAccessResponse, LockboxDetail, LockboxSummary } from "../../types";

export function useLockboxAccessDomain(params?: { skip?: boolean }): {
    data: LockboxAccessResponse | undefined;
    isLoading: boolean;
    isFetching: boolean;
} {
    const query = useLockboxAccessQuery(undefined, { skip: !!params?.skip });
    return {
        data: query.data,
        isLoading: query.isLoading,
        isFetching: query.isFetching
    };
}

export function useLockboxesByWalletDomain(wallet: string | undefined): {
    data: LockboxSummary[] | undefined;
    isLoading: boolean;
    isFetching: boolean;
} {
    const query = useLockboxesByWalletQuery(wallet ? { wallet } : skipToken, { skip: !wallet });
    return {
        data: query.data,
        isLoading: query.isLoading,
        isFetching: query.isFetching
    };
}

export function useLockboxDomain(address: string | undefined): {
    data: LockboxDetail | undefined;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    refetch: () => void;
} {
    const query = useLockboxQuery(address ? { address } : skipToken, { skip: !address });
    return {
        data: query.data,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isError: query.isError,
        refetch: query.refetch
    };
}

export function useLockboxByNftMintDomain(mint: string | undefined): {
    data: LockboxDetail | undefined;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    refetch: () => void;
} {
    const query = useLockboxByNftMintQuery(mint ? { mint } : skipToken, { skip: !mint });
    return {
        data: query.data,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isError: query.isError,
        refetch: query.refetch
    };
}
